import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection, getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisTodo, AdornisTodoStatus } from '@adornis/todo/db/AdornisTodo.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { CampusExercise } from '@chancenwerk/campus-learning-content/entities/CampusExercise.js';
import { CampusLesson } from '@chancenwerk/campus-learning-content/entities/CampusLesson.js';
import { CampusLevumiItemGerman } from '@chancenwerk/campus-learning-content/entities/levumi/CampusLevumiItemGerman.js';
import { CampusLevumiItemMath } from '@chancenwerk/campus-learning-content/entities/levumi/CampusLevumiItemMath.js';
import { CampusLevumiItemNumberDictation } from '@chancenwerk/campus-learning-content/entities/levumi/CampusLevumiItemNumberDictation.js';
import { CampusLevumiLevel } from '@chancenwerk/campus-learning-content/entities/levumi/CampusLevumiLevel.js';
import { LevumiItemFilter } from '@chancenwerk/campus-learning-content/entities/levumi/LevumiItemFilter.js';
import { PresenceRecord, Status } from '@chancenwerk/db/PresenceRecord.js';
import { CourseLernfoerderung } from '@chancenwerk/db/courses/CourseLernfoerderung.js';
import { LevumiMathCategory, LevumiSubject } from '@chancenwerk/db/enums/CampusLevumiEnums.js';
import { ContentResultEventlog, LevumiResultInformation } from '@chancenwerk/db/eventlogs/ContentResultEventlog.js';
import type { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { DateTime } from 'luxon';
import type { AggregationCursor } from 'mongodb';
import { catchError, from, of, switchMap } from 'rxjs';
import { StudentParticipationRelationship } from '../../db/semantics/StudentParticipationRelationship.js';
import { KPF } from '../../master-data-management/kpf/entities/KPF.js';
import { Partner } from '../../master-data-management/partner/entities/Partner.js';

@Entity()
export class SingleCumulativeLevumiStat extends MongoEntity {
  public static override _class = 'SingleCumulativeLevumiStat';

  @Field(type => Int)
  public count!: number;

  @Field(type => Float)
  public average!: number;
}

async function getCumulativeStatsAgg(
  startDate: Maybe<DateTime>,
  endDate: Maybe<DateTime>,
  bundeslaender: Maybe<string[]>,
  cities: Maybe<string[]>,
  kpfIDs: Maybe<string[]>,
  mathCategory: LevumiMathCategory,
  subject: string,
  level: string,
  count: Maybe<number>,
): Promise<AggregationCursor<{ stats: { count: number; average: number }[] }>> {
  // TODO include mathCategory in agg
  // TODO include level in agg
  // TODO there seems to be a problem when selecting all values for bundeslaender
  const coll = await getRawCollection<EntityData<ContentResultEventlog>>(ContentResultEventlog._collectionName);

  const agg = coll.aggregate<{ stats: { count: number; average: number }[] }>([
    // Preliminary matches
    {
      $match: {
        $and: [
          // Only get ContentResultEventLogs
          { _class: ContentResultEventlog._class },
          // Correctly answered percent can't be null
          { 'levumiResultInformation.correctlyAnswered': { $ne: null } },
          // Match on subject
          { 'levumiResultInformation.levumiLevel.subject': subject },
          // Match on math category
          { 'levumiResultInformation.levumiLevel.level': level },
          // Match on level
          ...(subject === LevumiSubject.MATH ? [{ 'levumiResultInformation.levumiMathCategory': mathCategory }] : []),
          // Match on start date
          ...(startDate ? [{ createdAt: { $gte: startDate.toJSDate() } }] : []),
          // Match on end date
          ...(endDate ? [{ createdAt: { $lte: endDate.toJSDate() } }] : []),
        ],
      },
    },
    // Sort oldest to newest
    {
      $sort: { createdAt: 1 },
    },
    // To match on bundeslaender, cities, and kpfIDs we require some more data (KPFs, Partners)
    // Lookup KPFs
    ...(kpfIDs?.length || cities?.length || bundeslaender?.length
      ? [
          // Lookup Student Participation Relationships -- required to lookup KPFs
          {
            $lookup: {
              from: StudentParticipationRelationship._collectionName,
              localField: 'userID',
              foreignField: 'subjectID',
              as: 'relationships',
            },
          },
          // Lookup KPFs
          {
            $lookup: {
              from: KPF._collectionName,
              localField: 'relationships.objectID',
              foreignField: '_id',
              as: 'kpfs',
              pipeline: [{ $project: { _id: 1, schoolID: 1 } }],
            },
          },
        ]
      : []),
    // Lookup Partners
    ...(cities?.length || bundeslaender?.length
      ? [
          {
            $lookup: {
              from: Partner._collectionName,
              localField: 'kpfs.schoolID',
              foreignField: '_id',
              as: 'partners',
              pipeline: [{ $project: { bundesland: 1, address: { zipCity: { city: 1 } } } }],
            },
          },
        ]
      : []),
    // Match KPF IDs
    ...(kpfIDs?.length ? [{ $match: { kpfs: { $elemMatch: { _id: { $in: kpfIDs } } } } }] : []),
    // Match on Bundeslaender
    ...(bundeslaender?.length
      ? [{ $match: { partners: { $elemMatch: { bundesland: { $in: bundeslaender } } } } }]
      : []),
    // Match on Cities
    ...(cities?.length ? [{ $match: { partners: { $elemMatch: { 'address.zipCity.city': { $in: cities } } } } }] : []),
    // Projection to score and user
    {
      $project: {
        userID: 1,
        levumiResultInformation: {
          correctlyAnswered: 1,
        },
      },
    },
    // Filter out 'null' scores
    { $match: { 'levumiResultInformation.correctlyAnswered': { $ne: null } } },
    // Grouping by user
    {
      $group: {
        _id: '$userID',
        scores: { $push: '$levumiResultInformation.correctlyAnswered' },
      },
    },
    // Filter out users with not equal 'count' scores
    ...(count
      ? [
          { $match: { $expr: { $gte: [{ $size: '$scores' }, count] } } },
          {
            $project: {
              _id: 1,
              userID: 1,
              scores: { $slice: ['$scores', count] },
            },
          },
        ]
      : []),
    // Collect to single number[][]
    {
      $group: {
        _id: null,
        allScores: { $push: '$scores' },
      },
    },
    // Transpose scores, filter out null values from resulting arrays
    {
      $project: {
        transposedScores: {
          $map: {
            input: {
              $range: [
                0,
                // Calculate max array size
                { $max: { $map: { input: '$allScores', as: 'arr', in: { $size: '$$arr' } } } },
              ],
            },
            as: 'idx',
            in: {
              $filter: {
                input: {
                  // Map index to respective elements in arrays
                  $map: {
                    input: '$allScores',
                    as: 'arr',
                    in: { $arrayElemAt: ['$$arr', '$$idx'] },
                  },
                },
                as: 'item',
                // Filter out null values
                cond: { $ne: ['$$item', null] },
              },
            },
          },
        },
      },
    },
    // Calculate Stats (Count and Average)
    {
      $project: {
        stats: {
          $map: {
            input: '$transposedScores',
            as: 'arr',
            in: {
              count: { $size: '$$arr' },
              average: { $avg: '$$arr' },
            },
          },
        },
      },
    },
  ]);

  return agg;
}

const resolveGetCumulativeLevumiStats = (
  startDate: Maybe<DateTime>,
  endDate: Maybe<DateTime>,
  bundeslaender: Maybe<string[]>,
  cities: Maybe<string[]>,
  kpfIDs: Maybe<string[]>,
  mathCategory: LevumiMathCategory,
  subject: Maybe<string>,
  level: Maybe<string>,
  count: Maybe<number>,
) => {
  return async (gqlFields: BaseQLSelectionSet<SingleCumulativeLevumiStat>) => {
    if (!subject || !level || (subject === LevumiSubject.MATH && !mathCategory)) return null;

    const agg = await getCumulativeStatsAgg(
      startDate,
      endDate,
      bundeslaender,
      cities,
      kpfIDs,
      mathCategory,
      subject,
      level,
      count,
    );

    return (await agg.toArray()).flatMap(v => v.stats).map(s => new SingleCumulativeLevumiStat(s));
  };
};

const getCumulativeLevumiStatsQuery = registerQuery({
  type: () => [SingleCumulativeLevumiStat],
  operationName: 'getCumulativeLevumiStats',
  params: [
    { type: () => DateTime, name: 'startDate' },
    { type: () => DateTime, name: 'endDate' },
    { type: () => [String], name: 'bundeslaender' },
    { type: () => [String], name: 'cities' },
    { type: () => [String], name: 'kpfIDs' },
    { type: () => String, name: 'mathCategory' },
    { type: () => String, name: 'subject' },
    { type: () => String, name: 'level' },
    { type: () => Int, name: 'count' },
  ],
  resolve: resolveGetCumulativeLevumiStats,
});

const resolveValidateLevumiExercise = (
  userID: string,
  contentID: string,
  lessonID: string,
  answer: Maybe<LevumiResultInformation>,
) => {
  return async (gqlFields: void) => {
    if (!answer) throw new Error('No Answer Object given!');

    const lessonCol = await getCollection(CampusLesson._class);
    const lesson = await lessonCol.findOne<CampusLesson>({ _id: lessonID });
    if (!lesson) throw new Error('Invalid lesson');
    const contentCol = await getCollection(CampusExercise._class);
    const content = await contentCol.findOne<CampusExercise>({ _id: contentID });
    if (!content) throw new Error('Invalid content id');
    const result = true;

    await new ContentResultEventlog({
      userID,
      answer: '',
      lessonID,
      contentID,
      result,
      type: content.type,
      levumiResultInformation: answer,
    }).create();

    // check for levumi reminder todos and check them
    const coll = await getRawCollection<EntityData<AdornisTodo>>(AdornisTodo._collectionName);

    await coll.updateMany(
      { _id: { $regex: `levumiTestReminderGenerator${userID.replaceAll(/([^\w])/g, '\\$1')}`, $options: 'i' } },

      {
        $set: { status: AdornisTodoStatus.CHECKED, checkedAt: DateTime.now().toJSDate() },
      },
    );

    return '';
  };
};

const validateLevumiExercise = registerMutation({
  type: () => String,
  operationName: 'validateLevumiExercise',
  params: [
    { type: () => String, name: 'userId' },
    { type: () => String, name: 'contentId' },
    { type: () => String, name: 'lessonId' },
    { type: () => LevumiResultInformation, name: 'answer' },
  ],
  resolve: resolveValidateLevumiExercise,
});

const resolveSubscribeLevumiItemsByLevel =
  (_class: string) =>
  <T extends MongoEntity>(levumiLevel: CampusLevumiLevel, category?: LevumiMathCategory) => {
    return (gqlFields: BaseQLSelectionSet<T>) => {
      const selector = { levelID: levumiLevel._id };
      if (category) {
        selector['category'] = category;
      }
      const collectionName = (baseqlMetaData.entities.resolveNode(_class).type() as typeof MongoEntity)._collectionName;
      const handle = getCollectionHandle<T>(collectionName);
      return from(publishFilter(_class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          return of([] as T[]);
        }),
      );
    };
  };

const subscribeLevumiGermanItemsByLevel = registerSubscription({
  type: () => [CampusLevumiItemGerman],
  operationName: 'subscribeLevumiGermanItemsByLevel',
  params: [{ type: () => CampusLevumiLevel, name: 'levumiLevel' }],
  resolve: resolveSubscribeLevumiItemsByLevel(CampusLevumiItemGerman._class)<CampusLevumiItemGerman>,
});

const subscribeLevumiMathItemsByLevel = registerSubscription({
  type: () => [CampusLevumiItemMath],
  operationName: 'subscribeLevumiMathItemsByLevel',
  params: [
    { type: () => CampusLevumiLevel, name: 'levumiLevel' },
    { type: () => String, name: 'category' },
  ],
  resolve: resolveSubscribeLevumiItemsByLevel(CampusLevumiItemMath._class)<CampusLevumiItemMath>,
});

const subscribeLevumiNumberDictateItemsByLevel = registerSubscription({
  type: () => [CampusLevumiItemNumberDictation],
  operationName: 'subscribeLevumiNumberDictateItemsByLevel',
  params: [{ type: () => CampusLevumiLevel, name: 'levumiLevel' }],
  resolve: resolveSubscribeLevumiItemsByLevel(CampusLevumiItemNumberDictation._class)<CampusLevumiItemNumberDictation>,
});

const resolveChangeLevumiLevelToUnspecified = (_class: string, levumiLevelID: string) => {
  return async (gqlFields: void) => {
    const collectionName = (baseqlMetaData.entities.resolveNode(_class).type() as typeof MongoEntity)._collectionName;
    const colLevumi = await getRawCollection(collectionName);

    const colLevel = await getCollection<CampusLevumiLevel>(CampusLevumiLevel._class);
    const levelToSet = await colLevel.findOne({ level: 'unspecified' });
    if (!levelToSet) throw new Error('unspecified level was not found! This should be ensured on startup');
    await colLevumi.updateMany({ levelID: levumiLevelID }, { $set: { levelID: levelToSet._id } });

    return true;
  };
};

const changeLevumiLevelToUnspecified = registerMutation({
  type: () => Boolean,
  operationName: 'changeLevumiLevelToUnspecified',
  params: [
    { type: () => String, name: '_class' },
    { type: () => String, name: 'levumiLevelID' },
  ],
  resolve: resolveChangeLevumiLevelToUnspecified,
});

const resolveGetNumberOfLevumiItems = (_class: string) => {
  return async (gqlFields: void) => {
    // ensure only the use of levumi classes
    if (
      _class !== CampusLevumiItemGerman._class &&
      _class !== CampusLevumiItemMath._class &&
      _class !== CampusLevumiItemNumberDictation._class
    ) {
      throw new Error('Operation not allowed');
    }
    const collectionName = (baseqlMetaData.entities.resolveNode(_class).type() as typeof MongoEntity)._collectionName;
    const col = await getRawCollection<CampusLevumiItemNumberDictation>(collectionName);
    return col.countDocuments();
  };
};

const getNumberOfLevumiItems = registerQuery({
  type: () => Int,
  operationName: 'getNumberOfLevumiItems',
  params: [{ type: () => String, name: '_class' }],
  resolve: resolveGetNumberOfLevumiItems,
});

const resolveSubscribeLevumiLevelsBySubject = (subject: string) => {
  return (gqlFields: BaseQLSelectionSet<CampusLevumiLevel>) => {
    const selector = { subject };
    const handle = getCollectionHandle<CampusLevumiLevel>(CampusLevumiLevel._collectionName);
    return from(publishFilter(CampusLevumiLevel._class)).pipe(
      switchMap(publishFilter => {
        return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
      }),
      catchError(e => {
        return of([] as CampusLevumiLevel[]);
      }),
    );
  };
};

const subscribeLevumiLevelsBySubject = registerSubscription({
  type: () => [CampusLevumiLevel],
  operationName: 'subscribeLevumiLevelsBySubject',
  params: [{ type: () => String, name: 'subject' }],
  resolve: resolveSubscribeLevumiLevelsBySubject,
});

const resolveLevumiLevelCurrentlyInUse = (levumiLevelID: string) => {
  return async (gqlFields: void) => {
    const colNumberDictate = await getRawCollection<CampusLevumiItemNumberDictation>(
      CampusLevumiItemNumberDictation._class,
    );
    const colMath = await getRawCollection<CampusLevumiItemNumberDictation>(CampusLevumiItemMath._class);
    const colGerman = await getRawCollection<CampusLevumiItemNumberDictation>(CampusLevumiItemGerman._class);

    const itemNumberDictate = await colNumberDictate.findOne<CampusLevumiItemNumberDictation>({
      levelID: levumiLevelID,
    });
    const itemMath = await colMath.findOne<CampusLevumiItemNumberDictation>({ levelID: levumiLevelID });
    const itemGerman = await colGerman.findOne<CampusLevumiItemNumberDictation>({ levelID: levumiLevelID });

    return !!(itemNumberDictate || itemMath || itemGerman);
  };
};

const getLevumiLevelCurrentlyInUse = registerQuery({
  type: () => [CampusLevumiLevel],
  operationName: 'getLevumiLevelCurrentlyInUse',
  params: [{ type: () => String, name: 'levumiLevelID' }],
  resolve: resolveLevumiLevelCurrentlyInUse,
});

const resolveSubscribeLevumiItem =
  (_class: string) =>
  <T extends MongoEntity>(skip: number, limit: number, filter: LevumiItemFilter) => {
    return (gqlFields: BaseQLSelectionSet<T>) => {
      if (!filter) return of([] as T[]);
      const selector = filter.getMongoFilter();
      const collectionName = (baseqlMetaData.entities.resolveNode(_class).type() as typeof MongoEntity)._collectionName;
      const handle = getCollectionHandle<T>(collectionName);
      return from(publishFilter(_class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector, {
            ...filter.getMongoOptions().sort,
            skip,
            limit,
          });
        }),
        catchError(e => {
          return of([] as T[]);
        }),
      );
    };
  };

const subscribeLevumiItemsGermanByFilter = registerSubscription({
  type: () => [CampusLevumiItemGerman],
  operationName: 'subscribeLevumiItemsGermanByFilter',
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => LevumiItemFilter, name: 'filter' },
  ],
  resolve: resolveSubscribeLevumiItem(CampusLevumiItemGerman._class)<CampusLevumiItemGerman>,
});

const subscribeLevumiItemsMathByFilter = registerSubscription({
  type: () => [CampusLevumiItemMath],
  operationName: 'subscribeLevumiItemsMathByFilter',
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => LevumiItemFilter, name: 'filter' },
  ],
  resolve: resolveSubscribeLevumiItem(CampusLevumiItemMath._class)<CampusLevumiItemMath>,
});

const subscribeLevumiItemsNumberDictateByFilter = registerSubscription({
  type: () => [CampusLevumiItemNumberDictation],
  operationName: 'subscribeLevumiItemsNumberDictateByFilter',
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => LevumiItemFilter, name: 'filter' },
  ],
  resolve: resolveSubscribeLevumiItem(CampusLevumiItemNumberDictation._class)<CampusLevumiItemNumberDictation>,
});

const _levumiLockStatus = async (studentID?: string) => {
  const user = studentID
    ? { _id: studentID, _class: Student._class }
    : await CurrentUserInfo.getMyself<ChancenwerkUser>()({
        _id: 1,
        _class: 1,
      });

  if (!user || user._class !== Student._class) {
    return {
      todo: [],
      status: 'WHITE',
    };
  }

  const student = await getByID(
    Student,
    user._id,
  )({
    schoolInformation: {
      jahrgangsstufe: 1,
    },
    kpfs: {
      _class: 1,
      produkt: {
        _class: 1,
      },
    },
  });

  const kpf = student?.kpfs?.[0];
  if (
    !kpf ||
    (student?.schoolInformation.jahrgangsstufe ?? 0) > 8 ||
    kpf.produkt?._class === 'ProductLUS' ||
    kpf.produkt?._class === 'ProductCAM'
  ) {
    return {
      todo: [],
      status: 'WHITE',
    };
  }

  // * Relevant sections that need to be done:
  const sections = await loadMongoData(CampusLesson, {
    name: {
      $in: ['N4', 'Station 2'],
    },
  });

  //
  // Check for initial levumi
  //
  const levumiResults = (await loadMongoData(
    ContentResultEventlog,
    {
      _class: ContentResultEventlog._class,
      userID: user._id,
      lessonID: {
        $in: sections.map(section => section._id),
      },
    },
    {
      sort: {
        createdAt: -1,
      },
    },
  )) as Maybe<EntityData<ContentResultEventlog>[]>;

  const neededIDs = sections
    .map(section => section._id)
    .filter(id => !levumiResults?.some(result => result.lessonID === id));

  // There are some levumi tests that were not done at least once
  if (neededIDs.length > 0) {
    return {
      todo: neededIDs,
      status: 'YELLOW',
    };
  }

  //
  // Check for levumi after 24 lefös
  //

  // Step 1. find the last levumi test of the sections
  const oldestLatest = Object.values(
    levumiResults?.reduce((prev, cur) => {
      if (prev[cur.lessonID]) {
        return prev;
      }
      prev[cur.lessonID] = cur.createdAt;
      return prev;
    }, {} as Record<string, Date>) ?? {},
  ).sort((a, b) => a.getTime() - b.getTime())[0];
  if (!oldestLatest) {
    return {
      todo: [],
      status: 'WHITE',
    };
  }

  // Step 2. check how many attendances are between the last levumi test and the current date
  const attendanceCol = await getRawCollection<EntityData<PresenceRecord>>(PresenceRecord._collectionName);
  const attendanceCount = await attendanceCol
    .aggregate([
      {
        $match: {
          $or: [
            { studentRecordsUS: { $elemMatch: { userID: user._id, status: Status.PRESENT } } },
            { studentRecordsOS: { $elemMatch: { userID: user._id, status: Status.PRESENT } } },
          ],
          date: {
            $gte: oldestLatest,
          },
        },
      },

      {
        $lookup: {
          from: 'courses',
          localField: 'courseID',
          foreignField: '_id',
          as: 'course',
        },
      },

      {
        $match: {
          'course._class': CourseLernfoerderung._class,
        },
      },

      {
        $project: {
          _id: 1,
        },
      },
    ])
    .toArray();

  // Step 3. if there are more than 24 attendances he/she needs to do it again
  if (attendanceCount.length < 24) {
    return {
      todo: [],
      status: 'WHITE',
    };
  }

  const levumiResultsAfter = (await loadMongoData(
    ContentResultEventlog,
    {
      _class: ContentResultEventlog._class,
      userID: user._id,
      lessonID: {
        $in: sections.map(section => section._id),
      },
      createdAt: {
        $gt: oldestLatest,
      },
    },
    {
      sort: {
        createdAt: -1,
      },
    },
  )) as Maybe<EntityData<ContentResultEventlog>[]>;

  return {
    todo: sections
      .map(section => section._id)
      .filter(id => !levumiResultsAfter?.some(result => result.lessonID === id)),
    status: attendanceCount.length >= 28 ? 'RED' : 'YELLOW',
  };
};

const subscribeForceLevumi = registerSubscription({
  type: () => [String],
  operationName: 'subscribeForceLevumi',
  resolve: () => {
    return () => {
      return watchQuery(ContentResultEventlog._collectionName, {
        _class: ContentResultEventlog._class,
      }).pipe(
        switchMap(async () => {
          return (await _levumiLockStatus()).todo;
        }),
      );
    };
  },
});

export namespace levumiAPI {
  /**
   * submit the levumi exercise. This uploads all answers and creates the result eventlog.
   */
  export const submitLevumiExercise = validateLevumiExercise;

  /**
   * changes the level to "unspecified" for every item of the given level. This enables the deletion of the given level and also takes out all levumi items out of the pool.
   * LevumiItems with the "unspecified" category can not be selected for exercises.
   * Use this mutation with causian
   */
  export const changeLevelToUnspecified = changeLevumiLevelToUnspecified;

  export const getNumberOfItems = getNumberOfLevumiItems;

  export const subscribeLevelsBySubject = subscribeLevumiLevelsBySubject;
  export const getLevelCurrentlyInUse = getLevumiLevelCurrentlyInUse;

  export const subscribeAllGermanByLevel = subscribeLevumiGermanItemsByLevel;
  export const subscribeAllMathByLevel = subscribeLevumiMathItemsByLevel;
  export const subscribeAllNumberDictateByLevel = subscribeLevumiNumberDictateItemsByLevel;

  export const subscribeGermanByFilter = subscribeLevumiItemsGermanByFilter;
  export const subscribeMathByFilter = subscribeLevumiItemsMathByFilter;
  export const subscribeNumberDictateByFilter = subscribeLevumiItemsNumberDictateByFilter;

  export const getCumulativeLevumiStats = getCumulativeLevumiStatsQuery;

  /**
   * Returns true if levumi should be forced. This is the case if the user has not completed the levumi tests yet.
   */
  export const subscribeForce = subscribeForceLevumi;

  /**
   * Returns the levumi lock status for a student.
   */
  export const levumiLockStatus = _levumiLockStatus;
}
